import { cn } from '@latitude/token-utils';
import {
  Content,
  Root as DialogRoot,
  Overlay,
  Portal,
} from '@radix-ui/react-dialog';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useRef } from 'react';

import { useBodyPointerEvents } from '../hooks/useBodyPointerEvents';
import { modalVariants } from '../variants/modal-variants';

export type yPosOption = 'top' | 'middle';

export type RootProps = {
  isOpen: boolean;
  onClose?: () => void;
  size?: 'small' | 'medium' | 'large';
  onInteractOutside?: ComponentPropsWithoutRef<
    typeof Content
  >['onInteractOutside'];
  /**
   * Runs before Escape closes the dialog. Call `e.preventDefault()` to keep it
   * open — the only way to hold a multi-step flow open when a stray keystroke
   * would discard work the user cannot recover.
   */
  onEscapeKeyDown?: ComponentPropsWithoutRef<typeof Content>['onEscapeKeyDown'];
  children: ReactNode;
  contentClassName?: string;
  yPosition?: yPosOption;
  'data-testid'?: string;
};

const Root: React.FC<RootProps> = ({
  isOpen,
  onClose,
  onInteractOutside,
  onEscapeKeyDown,
  size,
  children,
  contentClassName = '',
  yPosition,
  'data-testid': testId = 'modal-container',
}) => {
  useBodyPointerEvents(isOpen);

  const openerRef = useRef<HTMLElement | null>(null);

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={() => {
        onClose?.();
      }}
    >
      <Portal>
        <Overlay
          data-testid="modal-overlay"
          className={cn(
            'data-[state=open]:fade-in-0',
            'data-[state=open]:animate-in',
            'data-[state=closed]:fade-out-0',
            'data-[state=closed]:animate-out',
            'fixed',
            'inset-0',
            'z-[99]',
            'backdrop-blur',
          )}
          onClick={(e) => e.stopPropagation()}
        />
        <Content
          data-testid={testId}
          onOpenAutoFocus={(e) => {
            openerRef.current = document.activeElement as HTMLElement | null;
            // Focus the dialog itself, not Radix's first tabbable child (the
            // header close button, or a destructive footer action when the
            // header is hidden). Radix gives the content node tabIndex={-1}.
            e.preventDefault();
            (e.currentTarget as HTMLElement).focus();
          }}
          onCloseAutoFocus={(e) => {
            // Radix returns focus to the Dialog's Trigger, and this Modal is
            // controlled through `isOpen` with no Trigger to return to, so the
            // opener has to be restored here or focus falls back to body.
            e.preventDefault();
            openerRef.current?.focus({ preventScroll: true });
          }}
          className={cn([
            'z-[99]',
            modalVariants({
              size,
              yPos: yPosition,
            }),
            contentClassName,
          ])}
          onInteractOutside={(e) => {
            if (onInteractOutside) {
              onInteractOutside(e);
              return;
            }
            e.preventDefault();
          }}
          onEscapeKeyDown={onEscapeKeyDown}
        >
          {children}
        </Content>
      </Portal>
    </DialogRoot>
  );
};

export default Root;
